import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SideNavbar from './helpers/SideNavbar';
import defaultimage from "../assets/default-image.png";
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FaLink, FaCopy, FaEnvelope, FaCheck, FaChartLine, FaSync, FaFileAlt, FaCheckCircle, FaClock, FaTrophy, FaBell, FaRobot, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { BsFilePostFill } from "react-icons/bs";
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';

const AdminHome = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const UserID = localStorage.getItem('UserID');
  
  // Safety check: Redirect to login if no UserID
  useEffect(() => {
    if (!UserID) {
      console.warn('No UserID found, redirecting to login');
      navigate('/Login');
    }
  }, [UserID, navigate]);
  
  // Onboarding link generation state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: ''
  });
  
  const [generatedLink, setGeneratedLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isOnboardingAccordionOpen, setIsOnboardingAccordionOpen] = useState(false);
  
  // Today's Posts state
  const [todaysPosts, setTodaysPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [topPerformingPosts, setTopPerformingPosts] = useState([]);
  const [topPostsLoading, setTopPostsLoading] = useState(true);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [pendingPostsLoading, setPendingPostsLoading] = useState(true);
  const [approvedPosts, setApprovedPosts] = useState([]);
  const [approvedPostsLoading, setApprovedPostsLoading] = useState(true);
  const [knackFeeds, setKnackFeeds] = useState([]);
  const [knackFeedsLoading, setKnackFeedsLoading] = useState(false);
  const [knackChallenges, setKnackChallenges] = useState([]);
  const [challengesLoading, setChallengesLoading] = useState(true);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate onboarding link
  const generateLink = async () => {
    if (!formData.email) {
      Swal.fire({
        icon: 'warning',
        title: 'Email Required',
        text: 'Please enter an email address to generate the onboarding link.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-onboarding-link`, {
        ...formData,
        source: 'email' // Default to email invitation
      });
      
      if (response.data.success) {
        setGeneratedLink(response.data.shareableLink);
        Swal.fire({
          icon: 'success',
          title: 'Link Generated!',
          text: 'Your shareable onboarding link has been created successfully.',
          confirmButtonText: 'Great!',
          confirmButtonColor: '#2e3192'
        });
      } else {
        throw new Error('Failed to generate link');
      }
    } catch (error) {
      console.error('Error generating link:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate the onboarding link. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy link to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'The onboarding link has been copied to your clipboard.',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      Swal.fire({
        icon: 'error',
        title: 'Copy Failed',
        text: 'Could not copy to clipboard. Please copy manually.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
    }
  };

  // Send email invitation
  const sendEmail = async () => {
    if (!formData.email || !generatedLink) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please generate a link first and ensure email is provided.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/send-onboarding-email`, {
        email: formData.email,
        name: formData.name,
        company: formData.company,
        shareableLink: generatedLink,
        source: 'email' // Default to email invitation
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Email Sent!',
          text: `Email has been sent to ${formData.email} from vibes@knackhook.com`,
          confirmButtonText: 'Great!',
          confirmButtonColor: '#2e3192'
        });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Email Failed',
        text: 'Failed to send the invitation email. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
    }
  };

  // Helper function to check if media is a video
  const isVideoFile = (mediaPath) => {
    if (!mediaPath || typeof mediaPath !== 'string') return false;
    const pathWithoutQuery = mediaPath.split('?')[0];
    const fileExtension = pathWithoutQuery.split('.').pop()?.toLowerCase();
    const isVideo = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v', 'flv', 'wmv'].includes(fileExtension);
    const hasVideoInName = pathWithoutQuery.toLowerCase().includes('video');
    return isVideo || hasVideoInName;
  };

  // Helper function to get media URL
  const getMediaUrl = (mediaPath) => {
    if (!mediaPath) return null;
    const normalizedPath = mediaPath.replace(/\\/g, '/');
    const isFullUrl = normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://');
    const isAzureBlobUrl = normalizedPath.startsWith("https://sqlvaicpdkscvwnsyc.blob.core.windows.net/");
    const isLinkedInAsset = normalizedPath.includes('linkedin.com') || normalizedPath.includes('media.licdn.com');
    
    if (isFullUrl || isAzureBlobUrl || isLinkedInAsset) {
      return normalizedPath;
    } else {
      return `${process.env.REACT_APP_API_BASE_URL}/${normalizedPath}`;
    }
  };

  // Format scheduled time
  const formatScheduledTime = (value) => (value ? String(moment.parseZone(value, moment.ISO_8601, true).isValid() ? moment.parseZone(value, moment.ISO_8601, true).format('hh:mm A') : moment(value, 'HH:mm', true).isValid() ? moment(value, 'HH:mm', true).format('hh:mm A') : value).replace(' ', '\u00a0') : '');

  // Load today's posts
  const loadTodaysPosts = async () => {
    if (!UserID) return;

    setPostsLoading(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; 
      const todayDate = new Date().toLocaleDateString('en-CA');

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/todays-posts/${UserID}?timezone=${timezone}&date=${todayDate}`);

      let posts = response.data || [];
      if (!Array.isArray(posts)) {
        console.warn('Expected array but got:', typeof posts, posts);
        setTodaysPosts([]);
        return;
      }
      
      // Sort posts by status (pending, approved, posted) and then by time ascending
      posts.sort((a, b) => {
        const getStatusPriority = (post) => {
          const status = (post.Status || '').toString().toUpperCase();
          if (status === 'PENDING') return 1;
          if (status === 'APPROVED') return 2;
          if (status === 'POSTED') return 3;
          return 4;
        };

        const parseTime = (post) => {
          if (post.ScheduledTimeAsPerZone) {
            const timeStr = post.ScheduledTimeAsPerZone.toString();
            const timeMatch = timeStr.match(/(\d{2}):(\d{2})/);
            if (timeMatch) {
              const hours = parseInt(timeMatch[1], 10);
              const minutes = parseInt(timeMatch[2], 10);
              return hours * 60 + minutes;
            }
          }
          
          if (post.FormattedTime) {
            const timeStr = post.FormattedTime.toString();
            const timeMatch = timeStr.match(/(\d{2}):(\d{2})/);
            if (timeMatch) {
              const hours = parseInt(timeMatch[1], 10);
              const minutes = parseInt(timeMatch[2], 10);
              return hours * 60 + minutes;
            }
          }
          
          if (post.ScheduleTime) {
            const scheduleTime = new Date(post.ScheduleTime);
            if (!isNaN(scheduleTime.getTime())) {
              return scheduleTime.getHours() * 60 + scheduleTime.getMinutes();
            }
          }
          
          if (post.ScheduledPostDate) {
            const scheduledDate = new Date(post.ScheduledPostDate);
            if (!isNaN(scheduledDate.getTime())) {
              return scheduledDate.getHours() * 60 + scheduledDate.getMinutes();
            }
          }
          
          return -1;
        };
        
        const statusA = getStatusPriority(a);
        const statusB = getStatusPriority(b);
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        
        const timeA = parseTime(a);
        const timeB = parseTime(b);
        
        if (timeA >= 0 && timeB >= 0) {
          if (timeA !== timeB) {
            return timeA - timeB;
          }
        } else if (timeA >= 0 && timeB < 0) {
          return -1;
        } else if (timeA < 0 && timeB >= 0) {
          return 1;
        }
        
        const dateA = a.ScheduledPostDate || a.CreatedDate || new Date(0);
        const dateB = b.ScheduledPostDate || b.CreatedDate || new Date(0);
        return new Date(dateA) - new Date(dateB);
      });
      
      setTodaysPosts(posts);
    } catch (error) {
      console.error('Error loading today\'s posts:', error);
      setTodaysPosts([]);
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'warning',
          title: 'Unable to load posts',
          text: 'There was an issue loading today\'s posts. Please try refreshing.',
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          position: 'top-end'
        });
      }
    } finally {
      setPostsLoading(false);
    }
  };

  // Check if post is expired
  const isPostExpired = (post) => {
    if (!post?.ScheduledPostDate || !post?.ScheduledTimeAsPerZone) return false;
    const timePart = post.ScheduledTimeAsPerZone.split("T")[1].replace('Z', '');
    const combinedDateTimeStr = `${post.ScheduledPostDate.split("T")[0]}T${timePart}`;
    const scheduledDateTime = moment.tz(combinedDateTimeStr, 'YYYY-MM-DDTHH:mm:ss', post.TimeZone);
    const now = moment().tz(post.TimeZone);
    return scheduledDateTime.isBefore(now);
  };

  // Load top performing posts
  const loadTopPerformingPosts = async () => {
    if (!UserID) return;
    setTopPostsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/top-performing-posts/${UserID}`);
      const posts = response.data || [];
      if (!Array.isArray(posts)) {
        console.warn('Expected array but got:', typeof posts, posts);
        setTopPerformingPosts([]);
        return;
      }
      setTopPerformingPosts(posts);
    } catch (error) {
      console.error('Error loading top-performing posts:', error);
      setTopPerformingPosts([]);
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'warning',
          title: 'Unable to load top posts',
          text: 'There was an issue loading top-performing posts. Please try refreshing.',
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          position: 'top-end'
        });
      }
    } finally {
      setTopPostsLoading(false);
    }
  };

  // Load pending posts
  const loadPendingPosts = async () => {
    if (!UserID) return;
    setPendingPostsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getallaigeneratedpostsbyUserID/${UserID}`);
      const data = response.data || [];
      if (!Array.isArray(data)) {
        console.warn('Expected array but got:', typeof data, data);
        setPendingPosts([]);
        return;
      }
      const today = moment().startOf('day');
      const pendingPosts = data.filter(post => post && post.Status === 'PENDING' && !isPostExpired(post) && (!post.ScheduledPostDate || moment(post.ScheduledPostDate).startOf('day').isSameOrAfter(today)));
      const sortedPending = pendingPosts.sort((a, b) => {
        const dateA = a.ScheduledPostDate ? new Date(a.ScheduledPostDate).getTime() : 0;
        const dateB = b.ScheduledPostDate ? new Date(b.ScheduledPostDate).getTime() : 0;
        return dateA - dateB;
      });
      setPendingPosts(sortedPending || []);
    } catch (error) {
      console.error('Error loading pending posts:', error);
      setPendingPosts([]);
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'warning',
          title: 'Unable to load pending posts',
          text: 'There was an issue loading pending posts. Please try refreshing.',
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          position: 'top-end'
        });
      }
    } finally {
      setPendingPostsLoading(false);
    }
  };

  // Load approved posts
  const loadApprovedPosts = async () => {
    if (!UserID) return;
    setApprovedPostsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/approved-posts/${UserID}`);
      const posts = response.data || [];
      if (!Array.isArray(posts)) {
        console.warn('Expected array but got:', typeof posts, posts);
        setApprovedPosts([]);
        return;
      }
      setApprovedPosts(posts);
    } catch (error) {
      console.error('Error loading approved posts:', error);
      setApprovedPosts([]);
    } finally {
      setApprovedPostsLoading(false);
    }
  };

  // Load KnackFeeds
  const loadKnackFeeds = async () => {
    if (!UserID) return;
    setKnackFeedsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/knackfeeds/${UserID}`);
      const feeds = response.data?.data || [];
      if (!Array.isArray(feeds)) {
        console.warn('Expected array but got:', typeof feeds, feeds);
        setKnackFeeds([]);
        return;
      }
      setKnackFeeds(feeds);
    } catch (error) {
      console.error('Error loading KnackFeeds:', error);
      setKnackFeeds([]);
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'warning',
          title: 'Unable to load feeds',
          text: 'There was an issue loading the latest feeds. Please try refreshing.',
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          position: 'top-end'
        });
      }
    } finally {
      setKnackFeedsLoading(false);
    }
  };

  // Check if challenge is completed
  const isChallengeCompleted = (challenge) => {
    if (!challenge?.EndDate) return false;
    const endDate = moment(challenge.EndDate).startOf('day');
    const today = moment().startOf('day');
    return endDate.isBefore(today);
  };

  // Load KnackChallenges
  const loadKnackChallenges = async () => {
    if (!UserID) return;
    setChallengesLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getallchallenges?UserID=${UserID}`);
      const data = response.data || [];
      if (!Array.isArray(data)) {
        console.warn('Expected array but got:', typeof data, data);
        setKnackChallenges([]);
        return;
      }
      const activeChallenges = data.filter(challenge => challenge && !isChallengeCompleted(challenge));
      setKnackChallenges(activeChallenges);
    } catch (error) {
      console.error('Error loading KnackChallenges:', error);
      setKnackChallenges([]);
      if (error.response?.status !== 404) {
        Swal.fire({
          icon: 'warning',
          title: 'Unable to load challenges',
          text: 'There was an issue loading challenges. Please try refreshing.',
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          position: 'top-end'
        });
      }
    } finally {
      setChallengesLoading(false);
    }
  };

  // Load all data on mount
  useEffect(() => {
    if (UserID) {
      try {
        loadTodaysPosts();
        loadTopPerformingPosts();
        loadPendingPosts();
        loadApprovedPosts();
        loadKnackFeeds();
        loadKnackChallenges();
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Set all loading states to false to prevent infinite loading
        setPostsLoading(false);
        setTopPostsLoading(false);
        setPendingPostsLoading(false);
        setApprovedPostsLoading(false);
        setKnackFeedsLoading(false);
        setChallengesLoading(false);
      }
    }
  }, [UserID]);

  return (
    <>
      <SideNavbar>
        {loading ? <div className='spinner'></div>:
        <div className="card shadow-sm p-3" style={{ border: 'none', backgroundColor: '#fff' }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ border: 'none', backgroundColor: '#fff' }}>
            <h5 style={{ fontSize: '20px', color: '#2e3192', textAlign: 'left' }}>Welcome to Admin KnackVibes</h5>
          </div>
          <hr />
          
          {/* Onboarding Link Generation Section */}
          <div className="mb-4">
            <Card className="shadow-sm">
              <Card.Header 
                style={{ 
                  backgroundColor: '#2e3192', 
                  color: 'white',
                  cursor: 'pointer'
                }}
                onClick={() => setIsOnboardingAccordionOpen(!isOnboardingAccordionOpen)}
              >
                <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <FaLink className="me-2" />
                  Generate Shareable Onboarding Link
                </h6>
                  {isOnboardingAccordionOpen ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  )}
                </div>
              </Card.Header>
              {isOnboardingAccordionOpen && (
              <Card.Body className="p-4">
                <Alert variant="info" className="mb-3">
                  <small>
                    <strong>How it works:</strong> Generate a personalized onboarding link that you can share via email. When someone clicks the link, they'll be taken directly to the onboarding process with their information pre-filled.
                  </small>
                </Alert>

                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter recipient's email"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Enter recipient's name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Enter company name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="d-flex align-items-end">
                      <Button
                        variant="primary"
                        onClick={generateLink}
                        disabled={isGenerating || !formData.email}
                        style={{ backgroundColor: '#2e3192', borderColor: '#2e3192' }}
                        className="w-100"
                      >
                        {isGenerating ? 'Generating...' : 'Generate Link'}
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {generatedLink && (
                  <div className="mt-4">
                    <Alert variant="success">
                      <h6><FaCheck className="me-2" />Link Generated Successfully!</h6>
                      <p className="mb-2">Your shareable onboarding link:</p>
                      <div className="input-group">
                        <Form.Control
                          type="text"
                          value={generatedLink}
                          readOnly
                          className="font-monospace"
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={copyToClipboard}
                          title="Copy to clipboard"
                        >
                          {copied ? <FaCheck /> : <FaCopy />}
                        </Button>
                      </div>
                    </Alert>

                    <div className="d-grid">
                      <Button
                        variant="outline-primary"
                        onClick={sendEmail}
                        style={{ borderColor: '#2e3192', color: '#2e3192' }}
                      >
                        <FaEnvelope className="me-2" />
                        Send via Email
                      </Button>
                    </div>

                    <Alert variant="light" className="mt-3">
                      <small>
                        <strong>What happens next:</strong><br />
                        • The recipient will receive a personalized onboarding link<br />
                        • When they click the link, their information will be pre-filled<br />
                        • They'll go through the complete onboarding process<br />
                        • You can track their progress and completion
                      </small>
                    </Alert>
                  </div>
                )}
              </Card.Body>
              )}
            </Card>
          </div>
          
          <hr />
          
          {/* Today's Posts and Top Performing Posts Row */}
          <div className="row mb-3 mt-4">
            {/* Today's Posts Card */}
            <div className="col-lg-6 col-md-12 mb-2">
              <div className="card border-0 shadow-sm h-100" style={{
                borderRadius: '12px'
              }}>
                <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        background: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e9ecef'
                      }}>
                        <FaChartLine size={20} style={{ color: '#2e3192' }} />
                      </div>
                    </div>
                    <div>
                      <div className="d-flex align-items-center mb-1">
                        <h6 className="mb-0 fw-bold me-2" style={{
                          fontSize: '1.1rem',
                          color: '#2e3192'
                        }}>
                          Today's Posts
                        </h6>
                        {!postsLoading && (
                          <span className="badge bg-light text-dark" style={{ fontSize: '12px', fontWeight: '600' }}>
                            {todaysPosts.length}
                          </span>
                        )}
                      </div>
                      <small className="text-muted">Your recent content activity</small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={loadTodaysPosts}
                      disabled={postsLoading}
                      style={{
                        fontSize: '12px',
                        padding: '6px 12px',
                        borderRadius: '8px'
                      }}
                      title="Refresh today's posts"
                    >
                      <FaSync size={10} />
                    </button>
                    {postsLoading && (
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    )}
                    <button
                      className="btn btn-sm btn-light"
                      onClick={() => navigate('/todays-posts')}
                      style={{
                        fontSize: '12px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        color: '#2e3192',
                        border: '1px solid #fff',
                        fontWeight: '600'
                      }}
                    >
                      More
                    </button>
                  </div>
                </div>
                <div className="card-body" style={{
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {postsLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading posts...</span>
                      </div>
                      <p className="mt-2 text-muted small">Loading today's posts...</p>
                    </div>
                  ) : todaysPosts.length > 0 ? (
                    <div className="row">
                      {todaysPosts.slice(0, 3).map((post, index) => (
                        <div key={`todays-post-${post.ContentId}-${index}`} className="col-md-4 mb-3">
                          <div 
                            className="card border-0 shadow-sm h-100" 
                            style={{
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              padding: '12px',
                              height: '180px',
                              display: 'flex',
                              flexDirection: 'column',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                            }}
                            onClick={() => {
                              if (post.ContentId) {
                                if (post.PostType === 'challenge') {
                                  navigate('/KnackChallenges');
                                  return;
                                }
                                
                                const isKnackFeed = post.InterestsPromptID || post.PostType === 'knack_feed' || post.PostSource === 'knackfeed';
                                const contentId = post.ContentId || post.ContentID;
                                
                                if (isKnackFeed && contentId) {
                                  const status = (post.Status || '').toString().toUpperCase();
                                  if (status === 'POSTED' || (status === 'APPROVED' && post.LinkdinPostId && post.LinkdinPostId.trim() !== '')) {
                                    navigate(`/viewPostedAigeneratedschedulepost/${contentId}`);
                                  } else {
                                    navigate(`/updatelatesttrendingnews/${contentId}`);
                                  }
                                } else {
                                  const status = (post.Status || '').toString().toUpperCase();
                                  if (status === 'POSTED' || (status === 'APPROVED' && post.LinkdinPostId && post.LinkdinPostId.trim() !== '')) {
                                    navigate(`/viewPostedAigeneratedschedulepost/${post.ContentId}`);
                                  } else {
                                    navigate(`/updatependingpost/${post.ContentId}`);
                                  }
                                }
                              }
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }}
                          >
                            {/* Image/Poll Section */}
                            <div style={{ flex: '0 0 auto' }}>
                              {(() => {
                                const pollRequired = post.pollrequired === true || post.pollrequired === 1 || post.pollrequired === 'true' || post.pollrequired === '1';
                                const hasQuestionOptions = post.QuestionwithOptions && 
                                  (typeof post.QuestionwithOptions === 'string' ? post.QuestionwithOptions.trim() !== '' : post.QuestionwithOptions);
                                const isPoll = pollRequired || hasQuestionOptions;
                                
                                let pollData = null;
                                if (isPoll && post.QuestionwithOptions) {
                                  try {
                                    pollData = typeof post.QuestionwithOptions === 'string' 
                                      ? JSON.parse(post.QuestionwithOptions) 
                                      : post.QuestionwithOptions;
                                  } catch (e) {
                                    console.error('Error parsing poll data:', e);
                                  }
                                }
                                
                                if (isPoll && pollData) {
                                  return (
                                    <div className="mb-2">
                                      <div
                                        className="d-flex flex-column align-items-center justify-content-center"
                                        style={{
                                          width: '100%',
                                          height: '60px',
                                          backgroundColor: '#e3f2fd',
                                          border: '2px solid #2196f3',
                                          borderRadius: '6px'
                                        }}
                                      >
                                        <FaFileAlt size={16} className="text-primary mb-1" />
                                        <small className="text-muted" style={{ fontSize: '8px', textAlign: 'center' }}>
                                          No image
                                        </small>
                                      </div>
                                    </div>
                                  );
                                } else if ((post.PostImage || post.Image || post.image || post.ImageURL) && (post.PostImage || post.Image || post.image || post.ImageURL) !== '/assets/default-post-image.png') {
                                  const mediaPath = post.PostImage || post.Image || post.image || post.ImageURL;
                                  const mediaUrl = getMediaUrl(mediaPath);
                                  const isVideo = isVideoFile(mediaPath);
                                  
                                  if (isVideo && mediaUrl) {
                                    return (
                                      <div className="mb-2">
                                        <video
                                          src={mediaUrl}
 controls
                                          style={{
                                            width: '100%',
                                            height: '60px',
                                            objectFit: 'cover',
                                            borderRadius: '6px'
                                          }}
>
 Your browser does not support the video tag.
                                        </video>
                                      </div>
                                    );
                                  } else if (mediaUrl) {
                                    return (
                                      <div className="mb-2">
                                        <img
                                          src={mediaUrl}
                                          alt={post.ShortContent || 'Post Image'}
                                          style={{
                                            width: '100%',
                                            height: '60px',
                                            objectFit: 'cover',
                                            borderRadius: '6px'
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </div>
                                    );
                                  }
                                } else {
                                  return (
                                    <div className="mb-2">
                                      <div
                                        className="d-flex flex-column align-items-center justify-content-center"
                                        style={{
                                          width: '100%',
                                          height: '60px',
                                          backgroundColor: '#e3f2fd',
                                          border: '2px solid #2196f3',
                                          borderRadius: '6px'
                                        }}
                                      >
                                        <FaFileAlt size={16} className="text-primary mb-1" />
                                        <small className="text-muted" style={{ fontSize: '8px', textAlign: 'center' }}>
                                          No image
                                        </small>
                                      </div>
                                    </div>
                                  );
                                }
                              })()}
                            </div>

                            {/* Content Section */}
                            <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                              <div>
                                <div className="small fw-bold" style={{
                                  fontSize: '12px',
                                  color: '#2e3192',
                                  marginBottom: '4px',
                                  lineHeight: '1.3',
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical'
                                }}>
                                  {(() => {
                                    const pollRequired = post.pollrequired === true || post.pollrequired === 1 || post.pollrequired === 'true' || post.pollrequired === '1';
                                    const hasQuestionOptions = post.QuestionwithOptions && 
                                      (typeof post.QuestionwithOptions === 'string' ? post.QuestionwithOptions.trim() !== '' : post.QuestionwithOptions);
                                    const isPoll = pollRequired || post.QuestionwithOptions !== null;
                                  
                                    if (pollRequired) {
                                      return 'Poll';
                                    }
                                    return post.ShortContent || 'AI Generated Content';
                                  })()}
                                </div>
                                
                                {(() => {
                                  const pollRequired = post.pollrequired === true || post.pollrequired === 1 || post.pollrequired === 'true' || post.pollrequired === '1';
                                  const hasQuestionOptions = post.QuestionwithOptions && 
                                    (typeof post.QuestionwithOptions === 'string' ? post.QuestionwithOptions.trim() !== '' : post.QuestionwithOptions);
                                  const isPoll = pollRequired || hasQuestionOptions;
                                  
                                  if (isPoll && post.QuestionwithOptions) {
                                    let pollData = null;
                                    try {
                                      pollData = typeof post.QuestionwithOptions === 'string' 
                                        ? JSON.parse(post.QuestionwithOptions) 
                                        : post.QuestionwithOptions;
                                    } catch (e) {
                                      console.error('Error parsing poll data:', e);
                                    }
                                    
                                    if (pollData && pollData.question) {
                                      return (
                                        <div className="small text-muted mt-1" style={{
                                          fontSize: '11px',
                                          lineHeight: '1.3',
                                          overflow: 'hidden',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical'
                                        }}>
                                          {pollData.question}
                                        </div>
                                      );
                                    }
                                  }
                                  return null;
                                })()}
                                
                                <div className="d-flex justify-content-between align-items-center mt-1" style={{ fontSize: '10px' }}>
                                  <small className="text-muted">
                                    {post.ScheduledTimeAsPerZone ? formatScheduledTime(post.ScheduledTimeAsPerZone) : (post.FormattedTime || 'Today')}
                                  </small>
                                  {(() => {
                                    const raw = (post.Status || '').toString().toUpperCase();
                                    const hasLinkedin = !!(post.LinkdinPostId && post.LinkdinPostId.trim() !== '');
                                    const isKnackFeed = post.InterestsPromptID || post.PostType === 'knack_feed' || post.PostSource === 'knackfeed';
                                    
                                    if (isKnackFeed && (raw === 'PENDING' || raw === 'APPROVED')) {
                                      return (<FaCheckCircle size={12} style={{ color: '#0d6efd' }} title="Approved" />);
                                    }
                                    
                                    const isPosted = raw === 'POSTED' || (raw === 'APPROVED' && hasLinkedin);
                                    const isApproved = raw === 'APPROVED' && !hasLinkedin;
                                    const isPending = raw === 'PENDING';
                                    if (isPosted) return (<FaCheckCircle size={12} style={{ color: '#28a745' }} title="Posted" />);
                                    if (isApproved) return (<FaCheckCircle size={12} style={{ color: '#0d6efd' }} title="Approved" />);
                                    if (isPending) return (<FaClock size={12} style={{ color: '#ffc107' }} title="Pending" />);
                                    return (<FaClock size={12} style={{ color: '#6c757d' }} title={raw || 'Unknown'} />);
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-muted">
                        <BsFilePostFill size={32} className="mb-2" />
                        <p className="mb-0">No posts created today</p>
                        <small>Start creating content to see your posts here!</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top-Performing Posts Card */}
            <div className="col-lg-6 col-md-12 mb-2">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '12px'
                }}>
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center p-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #e9ecef'
                        }}>
                          <FaTrophy size={20} style={{ color: '#2e3192' }} />
                        </div>
                      </div>
                      <div>
                        <div className="d-flex align-items-center mb-1">
                          <h6 className="mb-0 fw-bold me-2" style={{
                            fontSize: '1.1rem',
                            color: '#2e3192'
                          }}>
                            Top Performing Posts
                          </h6>
                          {!topPostsLoading && (
                            <span className="badge bg-light text-dark" style={{ fontSize: '12px', fontWeight: '600' }}>
                              {topPerformingPosts.length}
                            </span>
                          )}
                        </div>
                        <small className="text-muted">Your best performing content</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={loadTopPerformingPosts}
                        disabled={topPostsLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px'
                        }}
                        title="Refresh top performing posts"
                      >
                        <FaSync size={10} />
                      </button>
                      {topPostsLoading && (
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => navigate('/topperformingposts')}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          color: '#2e3192',
                          border: '1px solid #fff',
                          fontWeight: '600'
                        }}
                      >
                        More
                      </button>
                    </div>
                  </div>
                  <div className="card-body" style={{
                    padding: '16px'
                  }}>
                    {topPostsLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading posts...</span>
                        </div>
                        <p className="mt-2 text-muted small">Loading top-performing posts...</p>
                      </div>
                    ) : topPerformingPosts.length > 0 ? (
                      <div className="row">
                        {topPerformingPosts.slice(0, 3).map((post, index) => (
                          <div key={`top-post-${post.ContentId}-${index}`} className="col-md-4 mb-3">
                            <div 
                              className="card border-0 shadow-sm h-100" 
                              style={{
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '12px',
                                height: '180px',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                              }}
                              onClick={() => {
                                if (post.LinkdinPostId || post.ContentId) {
                                  navigate(`/viewpostcomments/${post.LinkdinPostId || post.ContentId}`);
                                }
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <div style={{ flex: '0 0 auto' }}>
                                {(() => {
                                  const mediaPath = post.PostImage || post.Image || post.image || post.ImageURL;
                                  if (mediaPath && mediaPath !== '/assets/default-post-image.png') {
                                    const mediaUrl = getMediaUrl(mediaPath);
                                    const isVideo = isVideoFile(mediaPath);
                                    
                                    if (isVideo && mediaUrl) {
                                      return (
                                        <div className="mb-2">
                                          <video
                                            src={mediaUrl}
                                            controls
                                            style={{
                                              width: '100%',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px'
                                            }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                        </div>
                                      );
                                    } else if (mediaUrl) {
                                      return (
                                        <div className="mb-2">
                                          <img
                                            src={mediaUrl}
                                            alt={post.ShortContent || 'Top Post Image'}
                                            style={{
                                              width: '100%',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        </div>
                                      );
                                    }
                                  }
                                  return (
                                    <div className="mb-2">
                                      <div
                                        className="d-flex flex-column align-items-center justify-content-center"
                                        style={{
                                          width: '100%',
                                          height: '60px',
                                          backgroundColor: '#e3f2fd',
                                          border: '2px solid #2196f3',
                                          borderRadius: '6px'
                                        }}
                                      >
                                        <FaFileAlt size={16} className="text-primary mb-1" />
                                        <small className="text-muted" style={{ fontSize: '8px', textAlign: 'center' }}>
                                          No image
                                        </small>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                              <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div>
                                  <div className="small fw-bold" style={{
                                    fontSize: '12px',
                                    color: '#2e3192',
                                    marginBottom: '4px',
                                    lineHeight: '1.3',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                  }}>
                                    {post.ShortContent || 'Top Performing Content'}
                                  </div>
                                  <div className="small text-muted d-flex justify-content-between" style={{
                                    fontSize: '10px',
                                    lineHeight: '1.2',
                                    marginTop: '4px'
                                  }}>
                                    <span>{post.LikeCount !== undefined && post.LikeCount !== null ? `${post.LikeCount.toLocaleString()} likes` : 'High engagement'}</span>
                                    <span>{post.CommentCount !== undefined && post.CommentCount !== null ? `${post.CommentCount} comments` : 'Top performer'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-muted">
                          <FaTrophy size={32} className="mb-2" />
                          <p className="mb-0">No top-performing posts yet</p>
                          <small>Create and post content to see performance metrics!</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Pending Posts and Approved Posts Row */}
<div className="row mb-3">
              {/* Pending Posts Card */}
              <div className="col-lg-6 col-md-12 mb-2">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '12px'
                }}>
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center p-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #e9ecef'
                        }}>
                          <FaBell size={20} style={{ color: '#2e3192' }} />
                        </div>
                      </div>
                      <div>
                        <div className="d-flex align-items-center mb-1">
                          <h6 className="mb-0 fw-bold me-2" style={{
                            fontSize: '1.1rem',
                          color: '#2e3192'
                        }}>
                            Pending Posts
                          </h6>
                          {!pendingPostsLoading && (
                            <span className="badge bg-warning" style={{ fontSize: '12px', fontWeight: '600' }}>
                              {pendingPosts.length}
                            </span>
                          )}
                      </div>
                        <small className="text-muted">Awaiting your review</small>
                    </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={loadPendingPosts}
                        disabled={pendingPostsLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px'
                        }}
                        title="Refresh pending posts"
                      >
                        <FaSync size={10} />
                      </button>
                      {pendingPostsLoading && (
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                    <button
                      className="btn btn-sm btn-light"
                        onClick={() => navigate('/viewallpendingposts')}
                      style={{
                        fontSize: '12px',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        color: '#2e3192',
                        border: '1px solid #fff',
                        fontWeight: '600'
                      }}
                    >
                      More
                    </button>
                  </div>
                  </div>
                  <div className="card-body" style={{
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {pendingPostsLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-warning" role="status">
                          <span className="visually-hidden">Loading posts...</span>
                        </div>
                        <p className="mt-2 text-muted small">Loading pending posts...</p>
                      </div>
                    ) : pendingPosts.length > 0 ? (
                    <div className="row">
                        {pendingPosts.slice(0, 3).map((post, index) => {
                          let displayContent = post.Content || post.Prompts || 'AI Generated Content';
                          return (
                            <div key={`pending-post-${post.PostId}-${index}`} className="col-md-4 mb-3">
                              <div 
                                className="card border-0 shadow-sm" 
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  height: '180px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                                }}
                                onClick={() => {
                                  if (post.ContentId) {
                                    navigate(`/updatependingpost/${post.ContentId}`);
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                              >
                                <div className="mb-2">
                                  {(() => {
                                    const mediaPath = post.Image || post.PostImage || post.image || post.ImageURL;
                                    if (mediaPath && mediaPath !== '/assets/default-post-image.png' && mediaPath.trim() !== '') {
                                      const mediaUrl = getMediaUrl(mediaPath);
                                      const isVideo = isVideoFile(mediaPath);
                                      
                                      if (isVideo && mediaUrl) {
                                        return (
                                          <video
                                            src={mediaUrl}
                                            controls
                                            style={{
                                              width: '100%',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px'
                                            }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                        );
                                      } else if (mediaUrl) {
                                        return (
                                          <img
                                            src={mediaUrl}
                                            alt={post.Content || 'Pending Post Media'}
                                            style={{
                                              width: '100%',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        );
                                      }
                                    }
                                    return (
                                      <div
                                        className="d-flex flex-column align-items-center justify-content-center"
                                        style={{
                                          width: '100%',
                                          height: '60px',
                                          backgroundColor: '#e3f2fd',
                                          border: '2px solid #2196f3',
                                          borderRadius: '6px'
                                        }}
                                      >
                                        <FaFileAlt size={16} className="text-primary mb-1" />
                                        <small className="text-muted" style={{ fontSize: '8px', textAlign: 'center' }}>
                                          No image Selected
                                        </small>
                          </div>
                                    );
                                  })()}
                                </div>
                                <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                  <div className="small fw-bold mb-2" style={{
                                    fontSize: '12px',
                                    color: '#2e3192',
                                    maxHeight: '4.5em',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical'
                                  }}>
                                    {displayContent}
                                  </div>
                                  <div className="mt-2">
                                    <small className="text-muted" style={{ fontSize: '9px' }}>
                                      {post.ScheduledPostDate ? new Date(post.ScheduledPostDate).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        timeZone: 'UTC'
                                      }) : 'Recent'}
                                      &nbsp;{formatScheduledTime(post.ScheduledTimeAsPerZone) || 'Recent'}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-muted">
                          <FaFileAlt size={32} className="mb-2" />
                          <p className="mb-0">No pending posts</p>
                          <small>All your posts are scheduled or posted!</small>
                        </div>
                      </div>
                    )}
                  </div>
                        </div>
                      </div>

              {/* Approved Posts Card */}
              <div className="col-lg-6 col-md-12 mb-2">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '12px'
                }}>
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center p-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #e9ecef'
                        }}>
                          <FaChartLine size={20} style={{ color: '#2e3192' }} />
                            </div>
                          </div>
                      <div>
                        <div className="d-flex align-items-center mb-1">
                          <h6 className="mb-0 fw-bold me-2" style={{
                            fontSize: '1.1rem',
                            color: '#2e3192'
                          }}>
                            Approved Posts
                          </h6>
                          {!approvedPostsLoading && (
                            <span className="badge bg-success" style={{ fontSize: '12px', fontWeight: '600' }}>
                              {approvedPosts.length}
                            </span>
                          )}
                        </div>
                        <small className="text-muted">Recently approved content</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={loadApprovedPosts}
                        disabled={approvedPostsLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px'
                        }}
                        title="Refresh approved posts"
                      >
                        <FaSync size={10} />
                      </button>
                      {approvedPostsLoading && (
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => navigate('/viewallapprovedposts')}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          color: '#2e3192',
                          border: '1px solid #fff',
                          fontWeight: '600'
                        }}
                      >
                        More
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-3" style={{
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {approvedPostsLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Loading posts...</span>
                        </div>
                        <p className="mt-2 text-muted small">Loading approved posts...</p>
                      </div>
                    ) : approvedPosts.length > 0 ? (
                      <div className="row">
                        {approvedPosts.slice(0, 3).map((post, index) => {
                          const isKnackFeed = post.InterestsPromptID || post.PostSource === 'knackfeed';
                          const contentId = post.ContentId || post.ContentID;
                          
                          return (
                            <div key={`approved-post-${post.PostId || post.ContentId}-${index}`} className="col-md-4 mb-3">
                              <div 
                                className="card border-0 shadow-sm" 
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  height: '180px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                                }}
                                onClick={() => {
                                  if (isKnackFeed && contentId) {
                                    navigate(`/updatelatesttrendingnews/${contentId}`);
                                  } else {
                                    navigate(`/viewApprovedAigeneratedschedulepost/${post.ContentId || post.PostId}`);
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                              >
                                <div className="mb-2">
                                  {(() => {
                                    const mediaPath = post.Image || post.PostImage || post.image || post.ImageURL;
                                    const hasMedia = mediaPath && mediaPath !== '/assets/default-post-image.png' && mediaPath.trim() !== '';
                                    
                                    if (hasMedia) {
                                      const mediaUrl = getMediaUrl(mediaPath);
                                      const isVideo = isVideoFile(mediaPath);
                                      
                                      if (isVideo && mediaUrl) {
                                        return (
                                          <video
                                            src={mediaUrl}
                                            controls
                                            style={{
                                              width: '100%',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px'
                                            }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                        );
                                      } else if (mediaUrl) {
                                        return (
                                          <img
                                            src={mediaUrl}
                                            alt={post.Content || post.ShortContent || 'Approved Post Image'}
                                            style={{
                                              width: '100%',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '6px'
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        );
                                      }
                                    } else {
                                      return (
                                        <div
                                          className="d-flex flex-column align-items-center justify-content-center"
                                          style={{
                                            width: '100%',
                                            height: '60px',
                                            backgroundColor: '#e8f5e9',
                                            border: '2px solid #28a745',
                                            borderRadius: '6px'
                                          }}
                                        >
                                          <FaChartLine size={16} className="text-success mb-1" />
                                          <small className="text-muted" style={{ fontSize: '8px', textAlign: 'center' }}>
                                            Approved
                                          </small>
                            </div>
                                      );
                                    }
                                  })()}
                          </div>
                                <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                  <div className="small fw-bold mb-2" style={{
                                    fontSize: '12px',
                                    color: '#2e3192',
                                    maxHeight: '4.5em',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical'
                                  }}>
                                    {post.Content || post.ShortContent || post.Prompts || 'Approved Content'}
                        </div>
                                  <div className="mt-2">
                                    <small className="text-muted" style={{ fontSize: '9px' }}>
                                      {post.ScheduledPostDate ? new Date(post.ScheduledPostDate).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        timeZone: 'UTC'
                                      }) : 'Recent'} &nbsp;
                                      {formatScheduledTime(post.ScheduledTimeAsPerZone) || 'Recent'}
                                    </small>
                      </div>
                    </div>
                  </div>
                </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-muted">
                          <FaFileAlt size={32} className="mb-2" />
                          <p className="mb-0">No approved posts</p>
                          <small>Approvals will appear here.</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* KnackFeeds and KnackChallenges */}
            <div className="row mb-3">
              {/* KnackFeeds Card */}
              <div className="col-lg-6 col-md-12 mb-2">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '12px'
                }}>
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center p-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #e9ecef'
                        }}>
                          <FaRobot size={20} style={{ color: '#2e3192' }} />
                        </div>
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold" style={{
                          fontSize: '1.1rem',
                          color: '#2e3192'
                        }}>
                          Knack Feeds
                        </h6>
                        <small className="text-muted">AI-powered content feeds</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={loadKnackFeeds}
                        disabled={knackFeedsLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px'
                        }}
                        title="Refresh knack feeds"
                      >
                        <FaSync size={10} />
                      </button>
                      {knackFeedsLoading && (
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => navigate('/knackfeeds')}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          color: '#2e3192',
                          border: '1px solid #fff',
                          fontWeight: '600'
                        }}
                      >
                        More
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-3" style={{
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {knackFeedsLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading feeds...</span>
                        </div>
                        <p className="mt-2 text-muted small">Loading latest feeds...</p>
                      </div>
                    ) : knackFeeds.length > 0 ? (
                      <div className="row">
                        {knackFeeds.slice(0, 3).map((feed, index) => (
                          <div key={`knack-feed-${feed.id || index}-${index}`} className="col-md-4 mb-3">
                            <div 
                              className="card border-0 shadow-sm" 
                              style={{
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '12px',
                                minHeight: '180px',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                              }}
                              onClick={() => navigate(`/updatelatesttrendingnews/${feed.id || feed.ContentID}`)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }}
                            >
                              {feed.ImageURL ? (
                                <div className="mb-2">
                                  <img
                                    src={feed.ImageURL || '/assets/default-post-image.png'}
                                    alt={feed.Title || 'Feed Image'}
                                    style={{ width: '100%', height: '60px', objectFit: 'cover', borderRadius: '6px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              ) : (
                                <div className="mb-2">
                                  <div
                                    className="d-flex flex-column align-items-center justify-content-center"
                                    style={{ width: '100%', height: '60px', backgroundColor: '#e3f2fd', border: '2px solid #2196f3', borderRadius: '6px' }}
                                  >
                                    <FaRobot size={16} className="text-primary mb-1" />
                                    <small className="text-muted" style={{ fontSize: '8px', textAlign: 'center' }}>No image</small>
                                  </div>
                                </div>
                              )}
                              <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div 
                                  className="small fw-bold mb-2" 
                                  style={{ 
                                    fontSize: '12px', 
                                    color: '#2e3192', 
                                    maxHeight: '2.4em', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    display: '-webkit-box', 
                                    WebkitLineClamp: 2, 
                                    WebkitBoxOrient: 'vertical',
                                    textDecoration: 'none'
                                  }}
                                >
                                  {feed.InterestsPromptData || 'Interest Prompt'}
                                </div>
                                <div className="small text-muted" style={{ fontSize: '10px', maxHeight: '3em', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                                  {feed.Content || 'Stay updated with the latest trends and insights...'}
                                </div>
                                <div className="mt-2">
                                  <small className="text-muted" style={{ fontSize: '9px' }}>
                                    {feed.CreatedDate ? new Date(feed.CreatedDate).toLocaleDateString() : 'Recent'}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-muted">
                          <FaRobot size={32} className="mb-2" />
                          <p className="mb-0">No feeds available</p>
                          <small>Check back later for the latest updates!</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* KnackChallenges Card */}
              <div className="col-lg-6 col-md-12 mb-2">
                <div className="card border-0 shadow-sm h-100" style={{
                  borderRadius: '12px'
                }}>
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center p-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #e9ecef'
                        }}>
                          <FaTrophy size={20} style={{ color: '#2e3192' }} />
                        </div>
                      </div>
                      <div>
                        <div className="d-flex align-items-center mb-1">
                          <h6 className="mb-0 fw-bold me-2" style={{
                            fontSize: '1.1rem',
                            color: '#2e3192'
                          }}>
                            Knack Challenges
                          </h6>
                          {!challengesLoading && (
                            <span className="badge bg-primary" style={{ fontSize: '12px', fontWeight: '600' }}>
                              {knackChallenges.length}
                            </span>
                          )}
                        </div>
                        <small className="text-muted">Engage with community challenges</small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={loadKnackChallenges}
                        disabled={challengesLoading}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px'
                        }}
                        title="Refresh challenges"
                      >
                        <FaSync size={10} />
                      </button>
                      {challengesLoading && (
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      )}
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => navigate('/knackchallenges')}
                        style={{
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          color: '#2e3192',
                          border: '1px solid #fff',
                          fontWeight: '600'
                        }}
                      >
                        More
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-3" style={{
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {challengesLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading challenges...</span>
                        </div>
                        <p className="mt-2 text-muted small">Loading challenges...</p>
                      </div>
                    ) : knackChallenges.length > 0 ? (
                      <div className="row">
                        {knackChallenges.slice(0, 3).map((challenge, index) => (
                          <div key={`knack-challenge-${challenge.ChallengeID || index}-${index}`} className="col-md-4 mb-3">
                            <div 
                              className="card border-0 shadow-sm" 
                              style={{
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '12px',
                                height: '180px',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
                              }}
                              onClick={() => navigate('/KnackChallenges')}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              <div className="mb-2">
                                <div
                                  className="d-flex align-items-center justify-content-center"
                                  style={{
                                    width: '100%',
                                    height: '60px',
                                    backgroundColor: '#e8f5e8',
                                    border: '2px solid #28a745',
                                    borderRadius: '6px'
                                  }}
                                >
                                  <FaTrophy size={16} className="text-success mb-1" />
                                </div>
                              </div>
                              <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div className="small fw-bold mb-2" style={{
                                  fontSize: '12px',
                                  color: '#2e3192',
                                  maxHeight: '4.5em',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical'
                                }}>
                                  {challenge.ChallengeName || 'Challenge'}
                                </div>
                                <div className="mt-2">
                                  <small className="text-muted" style={{ fontSize: '9px' }}>
                                    {challenge.EndDate ? new Date(challenge.EndDate).toLocaleDateString() : 'Active'}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-muted">
                          <FaTrophy size={32} className="mb-2" />
                          <p className="mb-0">No active challenges</p>
                          <small>Check back later for new challenges!</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
</div>
        </div>
}
      </SideNavbar>
    </>
  );
};

export default AdminHome;
